"use client";

import { useEffect, useRef } from "react";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";

interface WithdrawalSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  successTitle?: string;
  successMessage?: string;
  successAdditionalInfo?: string;
  closeButtonText?: string;
}

export default function WithdrawalSuccessModal({
  isOpen,
  onClose,
  successTitle,
  successMessage,
  successAdditionalInfo,
  closeButtonText,
}: Readonly<WithdrawalSuccessModalProps>) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    modalRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.currentTarget === event.target) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      handleBackdropClick(event);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-spacing-l md:pt-spacing-xl md:pb-spacing-xxxl md:px-spacing-xxxl bg-black bg-opacity-50"
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      <div
        ref={modalRef}
        className="relative bg-surface-neutral-option-1 rounded-2xl w-full max-w-[336px] md:max-w-[416px] max-h-[90vh] overflow-y-auto p-spacing-l md:pt-spacing-xl md:pb-spacing-xxxl md:px-spacing-xxxl shadow-lg focus:outline-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby="success-modal-title"
        tabIndex={-1}
      >
        <div className="flex flex-col items-center">
          <div className="mb-spacing-xl">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M8.3807 0.264362C10.6418 -0.246477 13.0074 -0.013022 15.1248 0.930377C15.503 1.09904 15.6733 1.54233 15.5047 1.92061C15.3361 2.29876 14.8927 2.46889 14.5145 2.30049C12.6927 1.48876 10.6572 1.28781 8.71175 1.72725C6.76614 2.16684 5.01457 3.22368 3.71859 4.73995C2.42264 6.25624 1.65159 8.15098 1.52034 10.1413C1.38921 12.1314 1.90459 14.1107 2.99007 15.7839C4.07566 17.457 5.67297 18.7352 7.54378 19.4265C9.4148 20.1177 11.4601 20.1854 13.3729 19.6198C15.2855 19.0542 16.9638 17.8852 18.158 16.2878C19.3522 14.6904 19.9985 12.7499 19.9998 10.7556V9.83565C19.9998 9.42144 20.3356 9.08565 20.7498 9.08565C21.164 9.08573 21.4998 9.42149 21.4998 9.83565V10.7556L21.491 11.1901C21.4023 13.3545 20.6612 15.4458 19.3602 17.1862C17.9722 19.0429 16.0206 20.401 13.7977 21.0583C11.5748 21.7155 9.19867 21.637 7.02425 20.8337C4.84988 20.0304 2.99299 18.5449 1.73128 16.6003C0.469854 14.6558 -0.129115 12.3555 0.0232739 10.0427C0.175796 7.72962 1.07189 5.52751 2.57796 3.76534C4.08409 2.00317 6.11958 0.775256 8.3807 0.264362ZM20.2196 2.22432C20.5123 1.9319 20.9872 1.93192 21.2801 2.22432C21.5728 2.51699 21.5725 2.99189 21.2801 3.28487L11.2801 13.2946C11.1395 13.4353 10.9487 13.5143 10.7498 13.5144C10.5509 13.5144 10.3602 13.4353 10.2196 13.2946L7.21956 10.2946C6.92694 10.0017 6.92676 9.52689 7.21956 9.23409C7.5124 8.94171 7.98733 8.94161 8.28011 9.23409L10.7489 11.7028L20.2196 2.22432Z"
                fill="#008205"
              />
            </svg>
          </div>

          <h2
            id="success-modal-title"
            className="font-header-font text-m md:text-3xl text-text-title font-normal !leading-[28px] md:!leading-[34px] mb-spacing-xs md:mb-spacing-s text-center"
          >
            {successTitle}
          </h2>
          <div className="mb-spacing-l">
            {successMessage && (
              <div className="font-body-font text-xs font-normal text-text-body !leading-[27px] mb-[9px] text-center">
                <RichText richText={successMessage} id="success-message" />
              </div>
            )}

            {successAdditionalInfo && (
              <div className="font-body-font text-xs font-normal text-text-body !leading-[27px] text-center">
                <RichText
                  richText={successAdditionalInfo}
                  id="success-additional-info"
                />
              </div>
            )}
          </div>

          <LabelButtonWrapper
            id="withdrawal-success-close-button"
            data-testid="success-close-button"
            isDisabled={false}
            label={closeButtonText || ""}
            hierarchy="primary"
            className="w-full h-[48px] rounded-[120px] font-body-font text-xs font-semibold text-text-on-primary !leading-[18px]"
            type="button"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
}
